import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  TextField,
  Button,
  Chip,
  Divider,
} from "@mui/material";
import AddNewPayrollGroup from "./AddNewPayrollGroup";
import EditPayrollGroup from "./EditPayrollGroup";
import AssignPayrollGroup from "./AssignPayrollGroup";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentIcon from "@mui/icons-material/Assignment";

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(1.8, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.015),
  },

  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.045),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",

  "&::-webkit-scrollbar": {
    height: "8px",
    width: "8px",
  },

  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },

  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",

    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.4),
    },
  },
}));

function Payrollgroups() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [payrollGroups, setPayrollGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "asc",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayrollGroup, setSelectedPayrollGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const itemsPerPage = 5;
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";

  useEffect(() => {
    fetchPayrollGroups();
  }, []);

  const fetchPayrollGroups = async () => {
    try {
      setLoading(true);

      const res = await fetch("/api/support/payroll-groups");

      if (!res.ok) {
        throw new Error(
          `Failed to fetch payroll groups. Status: ${res.status}`,
        );
      }

      const data = await res.json();

      const list = Array.isArray(data) ? data : data.recordset || [];

      setPayrollGroups(list);
    } catch (error) {
      console.error("Error fetching payroll groups:", error);

      setPayrollGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }

    setSortConfig({
      key,
      direction,
    });
  };
  const filteredPayrollGroups = payrollGroups.filter((item) => {
    const search = searchTerm.toLowerCase().trim();

    if (!search) return true;

    return (
      item.id?.toString().toLowerCase().includes(search) ||
      item.PayrollGroup?.toLowerCase().includes(search) ||
      item.EmployeeGroupId?.toString().toLowerCase().includes(search) ||
      item.EmployeeGroup?.toLowerCase().includes(search)
    );
  });

  const sortedPayrollGroups = [...filteredPayrollGroups].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === null || aValue === undefined) {
      return 1;
    }

    if (bValue === null || bValue === undefined) {
      return -1;
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
    }

    const comparison = String(aValue)
      .toLowerCase()
      .localeCompare(String(bValue).toLowerCase());

    return sortConfig.direction === "asc" ? comparison : -comparison;
  });

  // ============================================
  // PAGINATION
  // ============================================
  const indexOfLastItem = currentPage * itemsPerPage;

  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentItems = sortedPayrollGroups.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayrollGroup(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddNew = () => {
    setAddModalOpen(true);
  };
  const handleEdit = () => {
    if (!selectedPayrollGroup) return;

    setEditModalOpen(true);
    handleMenuClose();
  };

  const handleAssign = () => {
    console.log("Assign payroll group:", selectedPayrollGroup);

    setAssignModalOpen(true);
    setAnchorEl(null);
  };

  const columns = [
    {
      id: "id",
      label: "ID",
      width: "10%",
    },
    {
      id: "PayrollGroup",
      label: "Payroll Group",
      width: "30%",
    },
    {
      id: "EmployeeGroup",
      label: "Employee Group",
      width: "40%",
    },
    {
      id: "actions",
      label: "Actions",
      width: "20%",
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: {
          xs: 2,
          sm: 3,
        },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: {
            xs: "stretch",
            md: "center",
          },
          mb: 3,
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 2,
        }}
      >
        {/* TITLE */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: PRIMARY_COLOR,
            }}
          >
            Payroll Groups
          </Typography>
        </Box>

        {/* SEARCH + ADD */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search payroll groups..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            sx={{
              width: {
                xs: "100%",
                sm: 280,
              },
            }}
          />

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{
              minHeight: 40,
              whiteSpace: "nowrap",
              px: 2,
            }}
          >
            Add New
          </Button>
        </Box>
      </Box>

      {/* RESULT COUNT */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing <strong>{filteredPayrollGroups.length}</strong>{" "}
          {filteredPayrollGroups.length === 1
            ? "payroll group"
            : "payroll groups"}
        </Typography>
      </Box>
      <ResponsiveTableContainer component={Paper} elevation={0}>
        <Table
          size={isMobile ? "small" : "medium"}
          sx={{
            minWidth: 650,
          }}
        >
          {/* TABLE HEADER */}
          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.primary.light,

                "& th": {
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  whiteSpace: "nowrap",
                  borderBottom: "none",
                  py: 1.8,
                },
              }}
            >
              {columns.map((column) => (
                <ModernTableCell
                  key={column.id}
                  onClick={() => {
                    if (column.id !== "actions") {
                      handleSort(column.id);
                    }
                  }}
                  sx={{
                    width: column.width,
                    cursor: column.id !== "actions" ? "pointer" : "default",

                    "&:hover":
                      column.id !== "actions"
                        ? {
                            backgroundColor: alpha("#fff", 0.08),
                          }
                        : {},
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.8}>
                    {column.label}

                    {sortConfig.key === column.id &&
                      column.id !== "actions" && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          sx={{
                            lineHeight: 0.5,
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.6rem",
                              opacity: sortConfig.direction === "asc" ? 1 : 0.4,
                            }}
                          >
                            ▲
                          </span>

                          <span
                            style={{
                              fontSize: "0.6rem",
                              opacity:
                                sortConfig.direction === "desc" ? 1 : 0.4,
                            }}
                          >
                            ▼
                          </span>
                        </Box>
                      )}
                  </Box>
                </ModernTableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* TABLE BODY */}
          <TableBody>
            {loading ? (
              <TableRow>
                <ModernTableCell
                  colSpan={4}
                  align="center"
                  sx={{
                    py: 6,
                  }}
                >
                  <CircularProgress size={28} />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1.5 }}
                  >
                    Loading payroll groups...
                  </Typography>
                </ModernTableCell>
              </TableRow>
            ) : currentItems.length > 0 ? (
              currentItems.map((item) => (
                <StyledTableRow key={item.id} hover>
                  {/* ID */}
                  <ModernTableCell>
                    <Chip
                      label={`#${item.id}`}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        color: PRIMARY_COLOR,
                        backgroundColor: alpha(PRIMARY_COLOR, 0.08),
                        borderRadius: "6px",
                      }}
                    />
                  </ModernTableCell>

                  {/* PAYROLL GROUP */}
                  <ModernTableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: PRIMARY_COLOR,
                      }}
                    >
                      {item.PayrollGroup || "—"}
                    </Typography>
                  </ModernTableCell>

                  {/* EMPLOYEE GROUP */}
                  <ModernTableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                          }}
                        >
                          {item.EmployeeGroup || "—"}
                        </Typography>

                        {item.EmployeeGroupId !== null &&
                          item.EmployeeGroupId !== undefined && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Employee Group ID: {item.EmployeeGroupId}
                            </Typography>
                          )}
                      </Box>
                    </Box>
                  </ModernTableCell>

                  {/* ACTIONS */}
                  <ModernTableCell>
                    <Tooltip title="More actions" arrow>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, item)}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          color: PRIMARY_COLOR,
                          borderRadius: "8px",

                          "&:hover": {
                            backgroundColor: alpha(PRIMARY_COLOR, 0.08),
                          },
                        }}
                      >
                        <MoreVertIcon
                          fontSize={isMobile ? "small" : "medium"}
                        />
                      </IconButton>
                    </Tooltip>
                  </ModernTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <ModernTableCell
                  colSpan={4}
                  align="center"
                  sx={{
                    py: 7,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    No payroll groups found
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Try changing your search criteria.
                  </Typography>
                </ModernTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>
      {filteredPayrollGroups.length > itemsPerPage && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <Pagination
            count={Math.ceil(filteredPayrollGroups.length / itemsPerPage)}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
            size={isMobile ? "small" : "medium"}
            sx={{
              "& .MuiPaginationItem-root": {
                "&.Mui-selected": {
                  bgcolor: PRIMARY_COLOR,

                  "&:hover": {
                    bgcolor: SECONDARY_COLOR,
                  },
                },
              },
            }}
          />
        </Box>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: "10px",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {/* EDIT */}
        <MenuItem
          onClick={handleEdit}
          sx={{
            gap: 1.5,
            py: 1.3,
            px: 2,

            "&:hover": {
              backgroundColor: alpha(PRIMARY_COLOR, 0.05),
            },
          }}
        >
          <EditIcon
            fontSize="small"
            sx={{
              color: PRIMARY_COLOR,
            }}
          />

          <Typography variant="body2">Edit</Typography>
        </MenuItem>

        <Divider />

        {/* ASSIGN */}
        <MenuItem
          onClick={handleAssign}
          sx={{
            gap: 1.5,
            py: 1.3,
            px: 2,

            "&:hover": {
              backgroundColor: alpha(SECONDARY_COLOR, 0.06),
            },
          }}
        >
          <AssignmentIcon
            fontSize="small"
            sx={{
              color: SECONDARY_COLOR,
            }}
          />

          <Typography variant="body2">Assign</Typography>
        </MenuItem>
      </Menu>
      <EditPayrollGroup
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedPayrollGroup(null);
        }}
        onSaveSuccess={fetchPayrollGroups}
        payrollGroup={selectedPayrollGroup}
      />
      <AssignPayrollGroup
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        payrollGroup={selectedPayrollGroup}
        onSaveSuccess={fetchPayrollGroups}
      />
      <AddNewPayrollGroup
        open={addModalOpen}
        onClose={() => {
          setAddModalOpen(false);
        }}
        onSuccess={() => {
          setAddModalOpen(false);
          fetchPayrollGroups();
        }}
      />
    </Box>
  );
}

export default Payrollgroups;
